import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import DocumentPickerModal from '../../components/DocumentPickerModal';
import ImageLightbox from '../../components/ImageLightbox';

const Feedback = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { projectId } = useParams();
  const isAdminView = location.pathname.startsWith('/admin');

  const [feedbackItems, setFeedbackItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newFeedback, setNewFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    title: '',
    description: '',
    file_url: '',
    document_id: null,
    status: 'pending',
    parent_id: null
  });

  const [comments, setComments] = useState({});
  /* eslint-disable no-unused-vars */
  const [documents, setDocuments] = useState([]); // Kept if needed for other logic, but modal handles its own fetching
  /* eslint-enable no-unused-vars */
  /* eslint-enable no-unused-vars */
  const [loadingComments, setLoadingComments] = useState({});
  const [selectedDocuments, setSelectedDocuments] = useState([]); // Array of full document objects
  const [showDocPicker, setShowDocPicker] = useState(false);

  // Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Versioning State
  const [activeVersions, setActiveVersions] = useState({}); // { parentId: currentFeedbackId }

  // Group items by parent_id
  const feedbackGroups = React.useMemo(() => {
    const groups = {};

    // First pass: Identify potential roots and structure
    feedbackItems.forEach(item => {
      if (item.parent_id) {
        if (!groups[item.parent_id]) {
          groups[item.parent_id] = [];
        }
        groups[item.parent_id].push(item);
      } else {
        if (!groups[item.id]) {
          groups[item.id] = [];
        }
        groups[item.id].push(item);
      }
    });

    // Sort versions within groups
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });

    return groups;
  }, [feedbackItems]);

  // Ensure active version is set for all groups
  useEffect(() => {
    const newActive = { ...activeVersions };
    let changed = false;
    Object.keys(feedbackGroups).forEach(rootId => {
      if (!newActive[rootId] && feedbackGroups[rootId].length > 0) {
        // Default to latest version (last in array)
        const versions = feedbackGroups[rootId];
        newActive[rootId] = versions[versions.length - 1].id;
        changed = true;
      }
    });
    if (changed) setActiveVersions(newActive);
  }, [feedbackGroups, activeVersions]);

  useEffect(() => {
    fetchFeedbackData();
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projectId]);

  const fetchDocuments = async () => {
    try {
      let currentProjectId = projectId;
      if (!isAdminView) {
        const { data: projectData } = await supabase
          .from('projects')
          .select('id')
          .eq('client_id', user.id)
          .single();
        if (projectData) currentProjectId = projectData.id;
      }

      if (currentProjectId) {
        const { data } = await supabase
          .from('documents')
          .select('id, name, file_url')
          .eq('project_id', currentProjectId);
        setDocuments(data || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchFeedbackData = async () => {
    try {
      let currentProjectId = projectId;

      if (!isAdminView) {
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('*')
          .eq('client_id', user.id)
          .single();

        if (projectError) throw projectError;
        currentProjectId = projectData.id;
      }

      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback')
        .select(`
          *,
          feedback_documents (
            document:documents (*)
          )
        `)
        .eq('project_id', currentProjectId)
        .order('created_at', { ascending: false });

      if (feedbackError) throw feedbackError;
      setFeedbackItems(feedbackData);

      // Fetch comments for all items (or could do lazy load)
      feedbackData.forEach(item => fetchComments(item.id));

    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (feedbackId) => {
    const { data } = await supabase
      .from('feedback_comments')
      .select(`
        *,
        user:users(full_name, role)
      `)
      .eq('feedback_id', feedbackId)
      .order('created_at', { ascending: true });

    setComments(prev => ({ ...prev, [feedbackId]: data || [] }));
  };

  const handleCreateFeedback = async (e) => {
    e.preventDefault();
    try {
      // Clean up form - remove empty strings
      const cleanedForm = {
        title: feedbackForm.title,
        description: feedbackForm.description || null,
        type: feedbackForm.type || 'other',
        status: feedbackForm.status || 'pending',
        submitted_by: user.id,
        parent_id: feedbackForm.parent_id || null
      };



      // 1. Create Feedback Item
      const { data: newFeedbackItem, error: createError } = await supabase
        .from('feedback')
        .insert([{
          project_id: projectId,
          ...cleanedForm
        }]) /* form now includes parent_id */
        .select()
        .single();

      if (createError) throw createError;

      // 2. Link Selected Documents
      if (selectedDocuments.length > 0) {
        const docLinks = selectedDocuments.map(doc => ({
          feedback_id: newFeedbackItem.id,
          document_id: doc.id
        }));

        const { error: linkError } = await supabase
          .from('feedback_documents')
          .insert(docLinks);

        if (linkError) console.error('Error linking documents:', linkError);
      }

      await fetchFeedbackData();
      setShowCreateModal(false);

      // Reset State
      setFeedbackForm({
        title: '',
        description: '',
        file_url: '',
        document_id: null,
        status: 'pending',
        parent_id: null
      });
      setSelectedDocuments([]);

    } catch (error) {
      console.error('Error creating feedback:', error);
      alert('Failed to create feedback: ' + error.message);
    }
  };

  const handleUpdateFeedback = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        title: feedbackForm.title,
        description: feedbackForm.description || null,
        type: feedbackForm.type || 'other',
        status: feedbackForm.status || 'pending'
      };

      // eslint-disable-next-line no-unused-vars
      const { data, error } = await supabase
        .from('feedback')
        .update(updateData)
        .eq('id', selectedItem.id)
        .select()
        .single();

      if (error) throw error;

      // Sync Documents
      // 1. Delete all existing links for this feedback item
      const { error: deleteError } = await supabase
        .from('feedback_documents')
        .delete()
        .eq('feedback_id', selectedItem.id);

      if (deleteError) throw deleteError;

      // 2. Insert new links
      if (selectedDocuments.length > 0) {
        const docLinks = selectedDocuments.map(doc => ({
          feedback_id: selectedItem.id,
          document_id: doc.id
        }));

        const { error: linkError } = await supabase
          .from('feedback_documents')
          .insert(docLinks);

        if (linkError) throw linkError;
      }

      await fetchFeedbackData();
      setShowEditModal(false);
      setSelectedItem(null);
      setSelectedDocuments([]); // Reset selection
    } catch (error) {
      console.error('Error updating feedback:', error);
      alert('Failed to update feedback: ' + error.message);
    }
  };



  const handleDocumentsSelected = (docs) => {
    setSelectedDocuments(prev => {
      // Merge with existing, avoiding duplicates
      const prevIds = new Set(prev.map(d => d.id));
      const newDocs = docs.filter(d => !prevIds.has(d.id));
      return [...prev, ...newDocs];
    });
    setShowDocPicker(false);
  };

  const removeSelectedDocument = (docId) => {
    setSelectedDocuments(prev => prev.filter(d => d.id !== docId));
  };


  const handleAddComment = async (feedbackId, content) => {
    if (!content.trim()) return;
    try {
      setLoadingComments(prev => ({ ...prev, [feedbackId]: true }));
      const { error } = await supabase
        .from('feedback_comments')
        .insert([{
          feedback_id: feedbackId,
          user_id: user.id,
          content: content.trim()
        }]);

      if (error) throw error;
      await fetchComments(feedbackId);
      setNewFeedback(''); // Clear input if multiple feedback areas share state, otherwise need local state
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setLoadingComments(prev => ({ ...prev, [feedbackId]: false }));
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback item?')) return;

    try {
      const { error } = await supabase
        .from('feedback')
        .delete()
        .eq('id', feedbackId);

      if (error) throw error;

      await fetchFeedbackData();
    } catch (error) {
      console.error('Error deleting feedback:', error);
      alert('Failed to delete feedback: ' + error.message);
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setFeedbackForm({
      title: item.title || '',
      description: item.description || '',
      file_url: item.file_url || '',
      status: item.status || 'pending'
    });

    // Pre-select existing documents
    if (item.feedback_documents && item.feedback_documents.length > 0) {
      const existingDocs = item.feedback_documents
        .filter(fd => fd.document)
        .map(fd => fd.document);
      setSelectedDocuments(existingDocs);
    } else {
      setSelectedDocuments([]);
    }

    setShowEditModal(true);
  };

  const openCreateVersionModal = (rootId, versions) => {
    const latest = versions[versions.length - 1]; // Use latest for title template
    setFeedbackForm({
      title: `${latest.title} (v${versions.length + 1})`,
      description: '',
      file_url: '',
      status: 'pending',
      parent_id: rootId
    });
    setSelectedDocuments([]);
    setShowCreateModal(true);
  };

  const handleSubmitFeedback = async (itemId, status) => {
    try {
      const { error } = await supabase
        .from('feedback')
        .update({ status, client_response: newFeedback, responded_at: new Date().toISOString() })
        .eq('id', itemId);

      if (error) throw error;

      fetchFeedbackData();
      setSelectedItem(null);
      setNewFeedback('');
      alert('Feedback submitted successfully!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback.');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'revision-requested': 'bg-orange-100 text-orange-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isImage = (fileName) => {
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName);
  };

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8 flex items-center justify-between"
      >
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Feedback & Approvals</h1>
          <p className="text-gray-600">
            {isAdminView ? 'Submit deliverables for client review and approval.' : 'Review deliverables and provide your feedback.'}
          </p>
        </div>
        {isAdminView && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
          >
            + New Feedback Request
          </button>
        )}
      </motion.div>

      <div className="grid grid-cols-1 gap-8">
        {Object.entries(feedbackGroups).map(([rootId, versions]) => {
          const activeId = activeVersions[rootId] || (versions.length > 0 ? versions[versions.length - 1].id : null);
          const item = versions.find(v => v.id === activeId) || versions[0];

          if (!item) return null;

          return (
            <motion.div
              key={rootId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-200 rounded-2xl p-6 relative"
            >
              {/* Thread Header / Version Tabs */}
              <div className="flex items-center justify-between mb-6 border-b border-gray-100 pb-4">
                <div className="flex items-center gap-2 overflow-x-auto">
                  {versions.map((v, idx) => (
                    <button
                      key={v.id}
                      onClick={() => setActiveVersions(prev => ({ ...prev, [rootId]: v.id }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${v.id === activeId
                        ? 'bg-black text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      v{idx + 1}
                    </button>
                  ))}
                  {isAdminView && (
                    <button
                      onClick={() => openCreateVersionModal(rootId, versions)}
                      className="ml-2 w-6 h-6 flex items-center justify-center rounded-full border border-dashed border-gray-400 text-gray-500 hover:border-black hover:text-black transition-colors"
                      title="New Version"
                    >
                      +
                    </button>
                  )}
                </div>

                {/* Status Badge in Top Right */}
                <span className={`absolute top-6 right-6 inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
                  {item.status.replace('-', ' ').toUpperCase()}
                </span>
              </div>

              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 pr-20"> {/* Padding for absolute badge */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                </div>
              </div>

              <p className="text-gray-600 mb-4">{item.description}</p>

              {/* Attached Documents via feedback_documents */}
              {item.feedback_documents && item.feedback_documents.length > 0 && (
                <div className="mb-4">
                  {/* 1. Images Grid (Thumbnails) */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.feedback_documents
                      .filter(fd => fd.document && isImage(fd.document.name))
                      .map((fd, imgIndex) => {
                        const doc = fd.document;
                        // Prepare array of all images for this feedback item for lightbox
                        const imagesInThisItem = item.feedback_documents
                          .filter(f => f.document && isImage(f.document.name))
                          .map(f => ({ url: f.document.file_url, name: f.document.name }));

                        return (
                          <div
                            key={doc.id}
                            onClick={() => openLightbox(imagesInThisItem, imgIndex)}
                            className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 cursor-zoom-in hover:opacity-90 transition-opacity"
                          >
                            <img
                              src={doc.file_url}
                              alt={doc.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        );
                      })}
                  </div>

                  {/* 2. Non-Image Documents (List) */}
                  <div className="space-y-2">
                    {item.feedback_documents
                      .filter(fd => fd.document && !isImage(fd.document.name))
                      .map((fd) => {
                        const doc = fd.document;
                        return (
                          <div key={doc.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-white rounded-md border border-gray-200">
                                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                  View Document
                                </a>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Backwards compatibility for single document_id if needed, or just hide it */}
              {item.document && !item.feedback_documents?.length && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-3">
                  <div className="p-2 bg-white rounded-md border border-gray-200">
                    <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.document.name}</p>
                    <a href={item.document.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                      View Document
                    </a>
                  </div>
                </div>
              )}

              {item.file_url && !item.document && (
                <a
                  href={item.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-black font-semibold hover:underline mb-4"
                >
                  View Deliverable
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}

              {/* Comments Thread */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-900 mb-4">Discussion</h4>

                <div className="space-y-4 mb-4">
                  {comments[item.id] && comments[item.id].length > 0 ? (
                    comments[item.id].map((comment) => (
                      <div key={comment.id} className="flex gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${comment.user_id === user.id ? 'bg-black text-white' : 'bg-gray-200 text-gray-600'
                          }`}>
                          {comment.user?.full_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 bg-gray-50 rounded-xl p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-gray-900">{comment.user?.full_name || 'Unknown User'}</span>
                            <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-700">{comment.content}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 italic">No comments yet.</p>
                  )}
                </div>

                {/* Add Comment: Locked for Client until Approved/Rejected */}
                {(isAdminView || item.status !== 'pending') ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Write a comment..."
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddComment(item.id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-50"
                      onClick={(e) => {
                        const input = e.target.previousSibling;
                        handleAddComment(item.id, input.value);
                        input.value = '';
                      }}
                      disabled={loadingComments[item.id]}
                    >
                      Send
                    </button>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-500 text-center italic border border-dashed border-gray-200">
                    Please approve or request revisions to start a discussion.
                  </div>
                )}

                {/* Action Buttons for Client (Approval) */}
                {!isAdminView && item.status === 'pending' && (
                  <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleSubmitFeedback(item.id, 'approved')}
                      className="flex-1 bg-green-50 text-green-700 border border-green-200 py-2 rounded-lg text-sm font-semibold hover:bg-green-100 transition-all"
                    >
                      Approve Deliverable
                    </button>
                    <button
                      onClick={() => handleSubmitFeedback(item.id, 'revision-requested')}
                      className="flex-1 bg-orange-50 text-orange-700 border border-orange-200 py-2 rounded-lg text-sm font-semibold hover:bg-orange-100 transition-all"
                    >
                      Request Revisions
                    </button>
                  </div>
                )}
              </div>

              {/* Footer / Meta */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Version {versions.indexOf(item) + 1} • {formatDate(item.created_at)}
                </p>

                {isAdminView && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(item)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteFeedback(item.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all text-sm"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {feedbackItems.length === 0 && (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <p className="text-gray-600">No feedback items yet.</p>
        </div>
      )}

      {/* Create Feedback Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Create Feedback Request</h3>
              <form onSubmit={handleCreateFeedback}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      value={feedbackForm.title}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Homepage Design Review"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
                    <textarea
                      value={feedbackForm.description}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      rows="3"
                      placeholder="Please review the homepage design and provide feedback..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Attached Documents</label>
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-2 mb-2">
                        {selectedDocuments.map(doc => (
                          <div key={doc.id} className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full text-sm">
                            <span className="truncate max-w-[150px]">{doc.name}</span>
                            <button
                              type="button"
                              onClick={() => removeSelectedDocument(doc.id)}
                              className="text-gray-500 hover:text-red-500"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowDocPicker(true)}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Attach Documents
                      </button>
                    </div>

                    <label className="block text-sm font-semibold text-gray-700 mb-2">Or External URL</label>
                    <input
                      type="url"
                      value={feedbackForm.file_url}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, file_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="https://figma.com/file/..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Version</label>
                      <input
                        type="number"
                        min="1"
                        value={feedbackForm.version}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, version: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                      <select
                        value={feedbackForm.status}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, status: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="revision-requested">Revision Requested</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-black hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors"
                  >
                    Create Request
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Feedback Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit Feedback Request</h3>
              <form onSubmit={handleUpdateFeedback}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      value={feedbackForm.title}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Homepage Design Review"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
                    <textarea
                      value={feedbackForm.description}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      rows="3"
                      placeholder="Please review the homepage design and provide feedback..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Attached Documents</label>
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-2 mb-2">
                        {selectedDocuments.map(doc => (
                          <div key={doc.id} className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full text-sm">
                            <span className="truncate max-w-[150px]">{doc.name}</span>
                            <button
                              type="button"
                              onClick={() => removeSelectedDocument(doc.id)}
                              className="text-gray-500 hover:text-red-500"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowDocPicker(true)}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Manage Documents
                      </button>
                    </div>

                    <label className="block text-sm font-semibold text-gray-700 mb-2">Deliverable URL</label>
                    <input
                      type="url"
                      value={feedbackForm.file_url}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, file_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="https://figma.com/file/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                    <select
                      value={feedbackForm.status}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="revision-requested">Revision Requested</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-black hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <DocumentPickerModal
        isOpen={showDocPicker}
        onClose={() => setShowDocPicker(false)}
        onConfirm={handleDocumentsSelected}
        projectId={projectId}
        initialSelectedIds={selectedDocuments.map(d => d.id)}
      />

      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        initialIndex={lightboxIndex}
      />
    </div >
  );
};

export default Feedback;
